/**
 * RunExports — documents & envoi pour une période approuvée/payée : bulletins PDF, chèques, registre, CPA-005, courriels. Batch #14.250.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FileText, Banknote, Table2, Landmark, Send, Mail, AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, fmtCAD, downloadFile, Btn, Section, inputCls, err } from "./_shared";
import PortalPublish from "./PortalPublish";

export default function RunExports({ masterId, run, settings, onChanged }) {
  const [busy, setBusy] = useState("");
  const [cpa, setCpa] = useState(null);
  const [batchEmail, setBatchEmail] = useState(settings.paystub_batch_email || "");
  const base = `/admin/payroll/${masterId}/runs/${run.id}`;

  const loadCpa = useCallback(() => {
    axios.get(`${API}${base}/cpa005/preview`, { headers: authH() }).then((r) => setCpa(r.data)).catch(() => setCpa(null));
  }, [base]);
  useEffect(() => { loadCpa(); }, [loadCpa, run.status]);

  const dl = (path, name, key) => downloadFile(`${base}/${path}`, name, (b) => setBusy(b ? key : ""));
  const send = async (mode) => {
    if (mode === "batch" && !batchEmail.includes("@")) { toast.error("Adresse courriel requise"); return; }
    if (!window.confirm(mode === "batch" ? `Envoyer tous les bulletins (1 PDF) à ${batchEmail} ?` : "Envoyer son bulletin par courriel à chaque employé (adresse de la fiche) ?")) return;
    setBusy(`send-${mode}`);
    try {
      const r = await axios.post(`${API}${base}/send-paystubs`, { mode, email: batchEmail }, { headers: authH() });
      if (r.data.failed) toast.error(`${r.data.sent} envoyé(s) · ${r.data.failed} échec(s) · ${r.data.skipped || 0} sans courriel`);
      else toast.success(`${r.data.sent} courriel(s) envoyé(s)${r.data.skipped ? ` · ${r.data.skipped} sans courriel` : ""}`);
      onChanged?.();
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const log = (run.delivery_log || []).slice(-6).reverse();
  return (
    <div className="space-y-4">
    <div className="grid lg:grid-cols-3 gap-4" data-testid="paie-run-exports">
      <Section title="Documents" icon={FileText} testId="paie-run-docs">
        <div className="flex flex-col gap-2">
          <Btn tone="slate" icon={FileText} busy={busy === "stubs"} onClick={() => dl("paystubs.pdf", `bulletins_${run.pay_date}.pdf`, "stubs")} testId="paie-dl-paystubs">Bulletins de paie (PDF, tous)</Btn>
          <Btn tone="slate" icon={Banknote} busy={busy === "cheques"} onClick={() => dl("cheques.pdf", `cheques_${run.pay_date}.pdf`, "cheques")} testId="paie-dl-cheques">Chèques (PDF · chèque + 2 talons)</Btn>
          <div className="text-[10px] text-slate-400">Employés payés par chèque seulement — numérotation automatique à partir de {settings.next_cheque_no || 1}.</div>
          <div className="flex gap-2 flex-wrap">
            <Btn tone="slate" icon={Table2} busy={busy === "reg-pdf"} onClick={() => dl("register.pdf", `registre_${run.pay_date}.pdf`, "reg-pdf")} testId="paie-dl-register-pdf">Registre PDF</Btn>
            <Btn tone="slate" icon={Table2} busy={busy === "reg-xlsx"} onClick={() => dl("register.xlsx", `registre_${run.pay_date}.xlsx`, "reg-xlsx")} testId="paie-dl-register-xlsx">Excel</Btn>
            <Btn tone="slate" icon={Table2} busy={busy === "reg-csv"} onClick={() => dl("register.csv", `registre_${run.pay_date}.csv`, "reg-csv")} testId="paie-dl-register-csv">CSV</Btn>
          </div>
        </div>
      </Section>

      <Section title="Dépôt direct — fichier CPA-005" icon={Landmark} testId="paie-run-cpa">
        {!cpa ? <p className="text-xs text-slate-500">Chargement…</p> : (
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between"><span className="text-slate-600">{cpa.nb} dépôt(s)</span><span className="font-mono font-bold">{fmtCAD(cpa.total)}</span></div>
            {cpa.missing_bank_info.length > 0 && (
              <div className="flex items-start gap-1.5 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="paie-cpa-missing">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Coordonnées bancaires manquantes : {cpa.missing_bank_info.join(", ")}
              </div>
            )}
            {!cpa.settings_ready && (
              <div className="flex items-start gap-1.5 text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="paie-cpa-settings-missing">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Réglages bancaires incomplets (n° d'expéditeur, centre de données, compte employeur) — tuile Réglages.
              </div>
            )}
            {cpa.file_no && <div className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Fichier n° {String(cpa.file_no).padStart(4, "0")} généré le {String(cpa.generated_at || "").slice(0, 10)}</div>}
            <Btn tone="orange" icon={Landmark} busy={busy === "cpa"} disabled={!cpa.settings_ready || cpa.nb === 0 || cpa.missing_bank_info.length > 0}
              onClick={() => dl("cpa005.txt", `cpa005_${run.pay_date}.txt`, "cpa").then(loadCpa)} testId="paie-dl-cpa005">
              {cpa.file_no ? "Retélécharger le fichier CPA-005" : "Générer le fichier CPA-005"}
            </Btn>
            <p className="text-[10px] text-slate-400">Format Standard 005 de Paiements Canada (1464 car.) — code de transaction 200 (paie). À transmettre via le service de transfert de fichiers de votre banque.</p>
          </div>
        )}
      </Section>

      <Section title="Envoi des bulletins" icon={Send} testId="paie-run-send">
        <div className="space-y-2">
          <Btn tone="sky" icon={Mail} busy={busy === "send-employees"} onClick={() => send("employees")} testId="paie-send-employees" className="w-full">Envoyer à chaque employé</Btn>
          <div className="flex gap-2">
            <input className={inputCls} placeholder="courriel@exemple.com" value={batchEmail} onChange={(e) => setBatchEmail(e.target.value)} data-testid="paie-send-batch-email" />
            <Btn tone="slate" icon={Send} busy={busy === "send-batch"} onClick={() => send("batch")} testId="paie-send-batch">Envoi groupé</Btn>
          </div>
          <p className="text-[10px] text-slate-400">Mode automatique configuré : <b>{settings.paystub_delivery === "auto_employee" ? "à chaque employé quand la période est marquée payée" : settings.paystub_delivery === "batch_one" ? `groupé à ${settings.paystub_batch_email}` : "aucun (téléchargement)"}</b>.</p>
          {log.length > 0 && (
            <ul className="text-[10px] text-slate-500 space-y-0.5 border-t border-slate-100 pt-2" data-testid="paie-delivery-log">
              {log.map((l, i) => <li key={i} className={l.ok ? "" : "text-rose-600"} data-testid={`delivery-log-${i}`}>{String(l.at).slice(0, 16).replace("T", " ")} · {l.mode === "batch" ? "groupé" : "employé"} → {l.to || "—"} · {l.ok ? "envoyé" : `échec (${l.info})`}{l.ok && l.protected && <Lock className="inline w-3 h-3 ml-1 text-emerald-700" title="PDF protégé par mot de passe" />}{l.ok && l.info && <span className="ml-1 text-amber-700">— {l.info}</span>}</li>)}
            </ul>
          )}
        </div>
      </Section>
    </div>
    <PortalPublish key={run.portal_published_at || "new"} masterId={masterId} run={run} settings={settings} onChanged={onChanged} />
    </div>
  );
}
